import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ToastrService } from 'ngx-toastr';

import * as bulkTopUpActions from './bulk-top-up.actions';
import { BulkTopUpService } from '../bulk-top-up.service';
import { catchError, exhaustMap, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { BulkPayService } from 'app/modules/bulk-pay/bulk-pay.service';
import { MiscService } from 'app/shared/misc.service';

@Injectable()
export class BulkTopUpEffects {
  constructor(
    private actions$: Actions,
    private bulkTopUpService: BulkTopUpService,
    private toastr: ToastrService,
     private miscService: MiscService,
  ) {}

      fetchCountries$ = createEffect(() =>
      this.actions$.pipe(
        ofType(bulkTopUpActions.fetchCountries),
        exhaustMap((action) =>
          this.miscService
            .getCountries()
            .pipe(
              exhaustMap((response: any) => [
                  bulkTopUpActions.fetchCountriesSuccess({ countries: response.data }),
              ]),
              catchError((error: HttpErrorResponse) => {
                  this.toastr.error(error.error?.errors, 'An error occured');
                  return of(bulkTopUpActions.fetchCountriesFailed())
              }
              )
            )
        )
      )
    );
  
  fetchRecipientCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bulkTopUpActions.fetchRecipientCategories),
      exhaustMap(({ page, limit }) =>
        this.bulkTopUpService.getRecipientsCategories(page, limit).pipe(
          map((response: any) => {
            const recipientCategories = response.data?.data || [];
            const pagination = response.data?.pagination || {};
            return bulkTopUpActions.fetchRecipientCategoriesSuccess({ recipientCategories, pagination });
          }),
          catchError((error: HttpErrorResponse) => {
            this.toastr.error(error.error?.message || 'An error occurred', 'Error');
            return of(bulkTopUpActions.fetchRecipientCategoriesFailed());
          })
        )
      )
    )
  );

    createRecipientCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bulkTopUpActions.createRecipientCategory),
      exhaustMap((action) =>
        this.bulkTopUpService
          .createRecipientsCategory(action.recipientCategoryDto)
          .pipe(
            exhaustMap((response: any) => [
                bulkTopUpActions.createRecipientCategorySuccess(),
            ]),
            catchError((error: HttpErrorResponse) => {
                this.toastr.error(error.error?.errors, 'An error occured');
                return of(bulkTopUpActions.createRecipientCategoryFailed())
            }
            )
          )
      )
    )
  );

    fetchRecipients$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bulkTopUpActions.fetchRecipients),
      exhaustMap((action) =>
        this.bulkTopUpService
          .getRecipients()
          .pipe(
            exhaustMap((response: any) => [
                bulkTopUpActions.fetchRecipientsSuccess({ recipients: response.data }),
            ]),
            catchError((error: HttpErrorResponse) => {
              if(error.status == 400){
                this.toastr.info( 'No Data');
                return of(bulkTopUpActions.fetchRecipientsSuccess({ recipients: [] }),)
              }else {
                this.toastr.error(error.error?.errors, 'An error occured');
                return of(bulkTopUpActions.fetchRecipientsFailed())
              }
            }
            )
          )
      )
    )
  );

  validateXcelAccount$ = createEffect(() =>
  this.actions$.pipe(
    ofType(bulkTopUpActions.validateXcelAccount),
    exhaustMap((action) =>
      this.miscService
        .validateXcelAccount(action.walletNumber, action.schemeCode)
        .pipe(
          exhaustMap((response: any) => [
              bulkTopUpActions.validateXcelAccountSuccess({ xcelAccount: response.data }),
          ]),
          catchError((error: HttpErrorResponse) => {
              this.toastr.error(error.error?.errors, 'An error occured');
              return of(bulkTopUpActions.validateXcelAccountFailed())
          }
          )
        )
    )
  )
  );
  

  createRecipient$ = createEffect(() =>
  this.actions$.pipe(
    ofType(bulkTopUpActions.createRecipient),
    exhaustMap((action) =>
      this.bulkTopUpService
        .createRecipient(action.recipientDto)
        .pipe(
          exhaustMap((response: any) => [
              bulkTopUpActions.createRecipientSuccess(),
          ]),
          catchError((error: HttpErrorResponse) => {
              this.toastr.error(error.error?.errors, 'An error occured');
              return of(bulkTopUpActions.createRecipientFailed())
          }
          )
        )
    ))
  );


  editRecipient$ = createEffect(() =>
  this.actions$.pipe(
    ofType(bulkTopUpActions.editRecipient),
    exhaustMap((action) =>
      this.bulkTopUpService
        .editRecipient(action.recipientDto)
        .pipe(
          exhaustMap((response: any) => [
              bulkTopUpActions.editRecipientSuccess(),
          ]),
          catchError((error: HttpErrorResponse) => {
              this.toastr.error(error.error?.errors, 'An error occured');
              return of(bulkTopUpActions.editRecipientFailed())
          }
          )
        )
    )
  )
  );

  deleteRecipient$ = createEffect(() =>
  this.actions$.pipe(
    ofType(bulkTopUpActions.deleteRecipient),
    exhaustMap((action) =>
      this.bulkTopUpService
        .deleteRecipient(action.recipientId)
        .pipe(
          exhaustMap((response: any) => [
              bulkTopUpActions.deleteRecipientSuccess(),
          ]),
          catchError((error: HttpErrorResponse) => {
              this.toastr.error(error.error?.errors, 'An error occured');
              return of(bulkTopUpActions.deleteRecipientFailed())
          }
          )
        )
    )
  )
  );

  fetchAccountBalance$ = createEffect(() =>
  this.actions$.pipe(
    ofType(bulkTopUpActions.fetchAccountBalance),
    exhaustMap((action) =>
      this.miscService
        .performBalanceInquiry(action.accountIdentifier)
        .pipe(
          exhaustMap((response: any) => [
              bulkTopUpActions.fetchAccountBalanceSuccess({ balance: response.data }),
          ]),
          catchError((error: HttpErrorResponse) => {
              this.toastr.error(error.error?.errors, 'An error occured');
              return of(bulkTopUpActions.fetchAccountBalanceFailed())
          }
          )
        )
    )
  )
  );

  
  sendOTP$ = createEffect(() =>
  this.actions$.pipe(
    ofType(bulkTopUpActions.sendOTP),
    exhaustMap((action) =>
      this.miscService
        .requestTransactionOTP(action.accountIdentifier)
        .pipe(
          exhaustMap((response: any) => [
              bulkTopUpActions.sendOTPSuccess({otpInfo: response.data}),
          ]),
          catchError((error: HttpErrorResponse) => {
              this.toastr.error(error.error?.errors, 'An error occured');
              return of(bulkTopUpActions.sendOTPFailed())
          }
          )
        )
    )
  )
  );
  
  updateTransactionStatus$ = createEffect(() =>
  this.actions$.pipe(
    ofType(bulkTopUpActions.updateTransactionStatus),
    exhaustMap((action) =>
      this.bulkTopUpService
        .updateTransactionStatus(action.updateTransactionStatusDto, action.transactionId, action.paymentId, action.status)
        .pipe(
          exhaustMap((response: any) => [
              bulkTopUpActions.updateTransactionStatusSuccess(),
          ]),
          catchError((error: HttpErrorResponse) => {
              this.toastr.error(error.error?.errors, 'An error occured');
              return of(bulkTopUpActions.updateTransactionStatusFailed())
          }
          )
        )
    )
  )
  );
  
  fetchRecipientBanks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bulkTopUpActions.fetchRecipientBanks),
        exhaustMap((action) =>
        this.bulkTopUpService
        .getRecipientBanks(action.countryCode)
        .pipe(
          exhaustMap((response: any) => [
              bulkTopUpActions.fetchRecipientBanksSuccess({ banks: response.data }),
          ]),
          catchError((error: HttpErrorResponse) => {
              this.toastr.error(error.error?.errors, 'An error occured, banks could not be fetched');
              return of(bulkTopUpActions.fetchRecipientBanksFailed())
          }
          )
        )
       )
      )
    );
  

}

